import React, { useState } from 'react';
import { Star, Send } from 'lucide-react';
import { supportService, CreateFeedbackData } from '../../lib/supportService';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Textarea } from '../common/Textarea';
import { Select } from '../common/Select';

interface FeedbackFormProps {
  onFeedbackSubmitted: () => void;
  onCancel: () => void;
}

export function FeedbackForm({ onFeedbackSubmitted, onCancel }: FeedbackFormProps) {
  const [formData, setFormData] = useState({
    feedback_type: 'general' as const,
    title: '',
    description: '',
    rating: 0,
    module: '',
    is_anonymous: false,
    allow_contact: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const feedbackData: CreateFeedbackData = {
        feedback_type: formData.feedback_type,
        title: formData.title.trim(),
        description: formData.description.trim(),
        rating: formData.rating || undefined,
        module: formData.module || undefined,
        is_anonymous: formData.is_anonymous,
        allow_contact: formData.allow_contact
      };

      await supportService.createFeedback(feedbackData);
      onFeedbackSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const feedbackTypeOptions = [
    { value: 'general', label: 'General Feedback' },
    { value: 'feature_feedback', label: 'Feature Feedback' },
    { value: 'ui_ux', label: 'UI/UX Feedback' },
    { value: 'performance', label: 'Performance' },
    { value: 'content', label: 'Content' },
    { value: 'suggestion', label: 'Suggestion' },
    { value: 'compliment', label: 'Compliment' },
    { value: 'complaint', label: 'Complaint' }
  ];

  const moduleOptions = [
    { value: '', label: 'Select a module (optional)' },
    { value: 'Financial Management', label: 'Financial Management' },
    { value: 'Health Management', label: 'Health Management' },
    { value: 'Legal Estate Planning', label: 'Legal Estate Planning' },
    { value: 'Digital Estate Planning', label: 'Digital Estate Planning' },
    { value: 'Calendar Management', label: 'Calendar Management' },
    { value: 'Travel Management', label: 'Travel Management' },
    { value: 'Pets Management', label: 'Pets Management' },
    { value: 'NDIS Management', label: 'NDIS Management' },
    { value: 'Wellbeing Management', label: 'Wellbeing Management' },
    { value: 'Legacy Management', label: 'Legacy Management' },
    { value: 'CryptoTradingPlatform', label: 'Crypto Trading Platform' },
    { value: 'Dashboard', label: 'Dashboard' },
    { value: 'General', label: 'General Platform' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <span className="text-red-800 text-sm">{error}</span>
        </div>
      )}

      {/* Feedback Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Feedback Type *
        </label>
        <Select
          value={formData.feedback_type}
          onChange={(value) => setFormData(prev => ({ ...prev, feedback_type: value as any }))}
          options={feedbackTypeOptions}
          placeholder="Select feedback type"
        />
      </div>

      {/* Module (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Module (optional)
        </label>
        <Select
          value={formData.module}
          onChange={(value) => setFormData(prev => ({ ...prev, module: value }))}
          options={moduleOptions}
          placeholder="Select a module"
        />
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title *
        </label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Brief summary of your feedback"
          maxLength={255}
        />
      </div>

      {/* Rating */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Rating (optional)
        </label>
        <div className="flex items-center space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
              className={`p-1 rounded ${
                star <= formData.rating
                  ? 'text-yellow-400 hover:text-yellow-500'
                  : 'text-gray-300 hover:text-gray-400'
              }`}
            >
              <Star size={20} fill={star <= formData.rating ? 'currentColor' : 'none'} />
            </button>
          ))}
          <span className="ml-2 text-sm text-gray-500">
            {formData.rating > 0 ? `${formData.rating}/5` : 'Click to rate'}
          </span>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Please provide detailed feedback..."
          rows={6}
        />
        <p className="text-xs text-gray-500 mt-1">
          Be as detailed as possible to help us improve the platform.
        </p>
      </div>

      {/* Options */}
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.is_anonymous}
            onChange={(e) => setFormData(prev => ({ ...prev, is_anonymous: e.target.checked }))}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">Submit anonymously</span>
        </label>
        
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.allow_contact}
            onChange={(e) => setFormData(prev => ({ ...prev, allow_contact: e.target.checked }))}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">Allow us to contact you for follow-up</span>
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          loading={loading}
          icon={<Send size={16} />}
        >
          Submit Feedback
        </Button>
      </div>
    </form>
  );
} 